import { faHouseFlag } from "@fortawesome/duotone-regular-svg-icons/faHouseFlag";
import { faMap } from "@fortawesome/duotone-regular-svg-icons/faMap";
import { faSearchLocation } from "@fortawesome/duotone-regular-svg-icons/faSearchLocation";
import { faUser } from "@fortawesome/duotone-regular-svg-icons/faUser";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Tooltip, UnstyledButton } from "@mantine/core";
import { getAuth } from "firebase/auth";
import { forwardRef, JSX, useEffect } from "react";
import { Link, Navigate, Route, Routes } from "react-router-dom";
import { useUser } from "reactfire";
import logo from "../../assets/logo.svg";
import { ROUTES } from "../../constants/routes";
import { useLoginEnabled } from "../../hooks/config/useLoginEnabled";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import { resetSearch } from "../../store/slices/campgroundSearch";
import { useAppDispatch } from "../../store/store";
import useIsRoute from "../../utils/useIsRoute";
import AppMenu from "../AppMenu";
import { openLoginModal } from "../auth/useOpenLoginModal";
import CampgroundDetailsModal from "../campground-details-modal/CampgroundDetailsModal";
import SearchResults from "../search-results/SearchResults";
import classes from "./Navbar.module.css";

interface NavbarLinkProps {
  label: string;
  active?: boolean;
  onClick?: () => void;
  icon?: IconDefinition;
  children?: JSX.Element;
}

export const NavbarButton = forwardRef<HTMLDivElement, NavbarLinkProps>(
  ({ icon: Icon, label, onClick, children }: NavbarLinkProps, ref) => {
    return (
      <Tooltip
        label={label}
        position="right"
        transitionProps={{ duration: 0 }}
        ref={ref}
      >
        <UnstyledButton
          variant="outline"
          onClick={onClick}
          className={classes.link}
        >
          {children
            ? children
            : Icon && <FontAwesomeIcon icon={Icon} className={classes.icon} />}
        </UnstyledButton>
      </Tooltip>
    );
  }
);

export const NavbarLink = forwardRef<HTMLDivElement, NavbarLinkProps>(
  ({ icon: Icon, label, onClick, children }: NavbarLinkProps, ref) => {
    const destination = ROUTES[label.toUpperCase() as keyof typeof ROUTES];
    const isActive = useIsRoute(destination);

    return (
      <Tooltip
        label={label}
        position="right"
        transitionProps={{ duration: 0 }}
        ref={ref}
      >
        <UnstyledButton
          variant="outline"
          onClick={onClick}
          data-active={isActive || undefined}
          className={classes.link}
          component={Link}
          to={destination} // Use routing path from constants
        >
          {children
            ? children
            : Icon && <FontAwesomeIcon icon={Icon} className={classes.icon} />}
        </UnstyledButton>
      </Tooltip>
    );
  }
);

const Navbar = () => {
  const user = useUser();
  const dispatch = useAppDispatch();
  const isLoginEnabled = useLoginEnabled();
  const isAdmin = useIsAdmin();
  const auth = getAuth();

  useEffect(() => {
    if (user.data && user.data.email && !isLoginEnabled) {
      auth.signOut();
    }
  }, [user, isLoginEnabled, auth]);

  return (
    <div className={classes.navbar}>
      <nav>
        <Box className={classes.linkGroup}>
          <Tooltip
            label={"i•wanna•camp!"}
            position="right"
            transitionProps={{ duration: 0 }}
            className={classes.logo}
          >
            <img
              src={logo}
              alt="I Wanna Camp Logo"
              style={{ maxWidth: "100%" }}
            />
          </Tooltip>
          <NavbarLink
            icon={faSearchLocation}
            label="Search"
            onClick={() => {
              dispatch(resetSearch());
            }}
          />
          <NavbarLink
            icon={faMap}
            label="Explore"
            onClick={() => {
              dispatch(resetSearch());
            }}
          />
          {/* <NavbarLink icon={faHeart} label="Favorites" /> */}
        </Box>

        <Box className={classes.linkGroup}>
          {(!user.data || !user.data.email) && isLoginEnabled && (
            <NavbarButton
              icon={faUser}
              label="Login"
              onClick={openLoginModal}
            />
          )}
          {isAdmin && (
            <Tooltip
              label="Admin"
              position="right"
              transitionProps={{ duration: 0 }}
            >
              <UnstyledButton
                variant="outline"
                className={classes.link}
                component={Link}
                to="/ranger-station"
              >
                <FontAwesomeIcon icon={faHouseFlag} className={classes.icon} />
              </UnstyledButton>
            </Tooltip>
          )}
          <AppMenu />
        </Box>
      </nav>

      <Routes>
        <Route index element={<Navigate replace to={ROUTES.SEARCH} />} />
        <Route path={ROUTES.SEARCH} element={<></>} />
        <Route path={ROUTES.EXPLORE} element={<></>} />
        <Route path="campground/:id" element={<CampgroundDetailsModal />} />
      </Routes>

      <SearchResults />
    </div>
  );
};

export default Navbar;
